import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useMe } from '@/hooks/useMe';
import { formatCents } from '@/utils/money';
import { SPORTS } from './sports';
import { BetSlipProvider } from '@/features/bets/BetSlipProvider';
import { BetSlip } from '@/features/bets/BetSlip';
import { BetSlipSheet } from '@/features/bets/BetSlipSheet';
import { BetPlacedToast } from '@/features/bets/BetPlacedToast';
import { LEGAL_DOCS, legalPath } from '@/features/legal/content';

const NavIcon = {
  bet: <><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="3.4" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3" /></>,
  deposit: <><rect x="3" y="6" width="18" height="13" rx="2.5" /><path d="M3 10h18M12 13.5v3M10.5 15h3" /></>,
  savings: <><rect x="5" y="10.5" width="14" height="9.5" rx="2.5" /><path d="M8.5 10.5V8a3.5 3.5 0 1 1 7 0v2.5" /><circle cx="12" cy="15" r="1.3" /></>,
  account: <><circle cx="12" cy="8.5" r="3.6" /><path d="M5 20c0-3.6 3.1-5.5 7-5.5s7 1.9 7 5.5" /></>,
  home: <><path d="M4 11l8-6 8 6M6 10v9h12v-9" /></>,
  ticket: <><path d="M4 8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4V8z" /><path d="M10 6v12" strokeDasharray="2 2" /></>,
} satisfies Record<string, ReactNode>;

function Glyph({ icon, size = 20 }: { icon: ReactNode; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      {icon}
    </svg>
  );
}

/**
 * The footer link list is DERIVED from `LEGAL_DOCS`, never hand-maintained
 * beside it. A second list is a second place to forget a document, and the
 * failure is silent — the page still exists, nothing links to it, and nobody
 * finds out. `content.test.ts` pins the titles unique and `legalPath()` in
 * agreement with the route the router declares, so label and destination
 * cannot drift apart here.
 */
function Footer() {
  return (
    <footer className="max-w-3xl xl:max-w-none mx-auto w-full px-4 lg:px-7 pb-8 pt-2">
      <div className="border-t border-white/[0.06] pt-5 text-xs text-white/35">
        <p className="mb-2">© {new Date().getFullYear()} Puntsave Pty Ltd · ABN [TBC] · Australia.</p>
        <div className="flex flex-wrap gap-x-4 gap-y-1.5">
          {LEGAL_DOCS.map((doc) => (
            <Link key={doc.slug} to={legalPath(doc.slug)} className="hover:text-white/60 transition">
              {doc.title}
            </Link>
          ))}
        </div>
        <p className="mt-3 text-white/25 max-w-2xl leading-relaxed">
          Gamble responsibly. Puntsave is a capital-preservation betting product — your deposits stay yours,
          moving between your locked Savings and withdrawable Play Balance as bets settle.
        </p>
      </div>
    </footer>
  );
}

/*
  The prototype's bottom bar: Home · Sports · My bets · Save · Profile
  (stakeholder feedback 2026-10-13). Deposit is not a tab — the green Deposit
  button in the header is on every page already. `end` keeps Home from
  lighting up on /bets/history and /bets/event/… too.
*/
const mobileNav = [
  { to: '/bets', label: 'Home', icon: NavIcon.home, end: true },
  { to: '/sports', label: 'Sports', icon: NavIcon.bet, end: false },
  { to: '/bets/history', label: 'My bets', icon: NavIcon.ticket, end: false },
  { to: '/savings', label: 'Save', icon: NavIcon.savings, end: false },
  { to: '/account', label: 'Profile', icon: NavIcon.account, end: false },
];

function Logo() {
  return (
    <span className="flex items-center gap-2.5">
      <span className="w-8 h-8 rounded-[9px] grid place-items-center bg-gradient-to-br from-brand-teal to-brand-mint text-brand-navy shadow-glow font-display font-extrabold text-[18px] leading-none">P</span>
      <span className="font-display font-extrabold text-[17px] tracking-[0.06em] text-white">PUNTSAVE</span>
    </span>
  );
}

export function AppShell() {
  const { data: me } = useMe();
  const navigate = useNavigate();
  const savings = me?.balances.savingsCents ?? 0;
  const playable = me?.balances.balanceCents ?? 0;

  return (
    /*
      The slip provider wraps the WHOLE shell, not BetsPage, because the slip is
      rendered in two places that are siblings: the desktop right rail below, and
      inline under the market grid inside <Outlet />. State held inside BetsPage
      is unreachable from the rail — which is exactly why clicking a selection
      appeared to do nothing on desktop.
    */
    <BetSlipProvider>
    <div className="h-screen flex flex-col">
      {/* Top bar */}
      <header className="shrink-0 h-[60px] grid grid-cols-[auto_1fr_auto] lg:grid-cols-[240px_1fr_auto] items-center gap-3 border-b border-white/[0.07] bg-gradient-to-b from-brand-navy2 to-brand-navy px-4 lg:pl-4 lg:pr-6">
        <div className="pl-1"><Logo /></div>
        <div className="relative hidden sm:block max-w-xl">
          <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
          <input
            placeholder="Search teams, races, markets…"
            className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl pl-10 pr-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-brand-teal/40 focus:border-brand-teal/40"
          />
        </div>
        <div className="flex items-center gap-2 justify-end">
          {/* Icon cluster — matches the prototype topbar density */}
          <div className="hidden md:flex items-center gap-0.5 mr-1">
            <button
              onClick={() => navigate('/savings')}
              className="w-9 h-9 rounded-lg grid place-items-center text-white/45 hover:text-white hover:bg-white/[0.06] transition"
              aria-label="Activity"
            >
              <Glyph icon={<><path d="M3 3v5h5" /><path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" /></>} size={18} />
            </button>
            <button
              onClick={() => navigate('/account')}
              className="w-9 h-9 rounded-lg grid place-items-center text-white/45 hover:text-white hover:bg-white/[0.06] transition"
              aria-label="Settings"
            >
              <Glyph icon={<><circle cx="12" cy="12" r="3.2" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9 7 7M17 17l2.1 2.1M19.1 4.9 17 7M7 17l-2.1 2.1" /></>} size={18} />
            </button>
            <button
              className="relative w-9 h-9 rounded-lg grid place-items-center text-white/45 hover:text-white hover:bg-white/[0.06] transition"
              aria-label="Notifications"
            >
              <Glyph icon={<><path d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" /></>} size={18} />
              <span className="absolute top-2 right-2 w-1.5 h-1.5 rounded-full bg-brand-teal" />
            </button>
          </div>
          <div className="hidden sm:flex flex-col items-end leading-none mr-1">
            <span className="text-[10px] uppercase tracking-[0.08em] text-white/35">To play</span>
            <span className="text-sm font-semibold tabular-nums">{formatCents(playable)}</span>
          </div>
          <button
            onClick={() => navigate('/deposit')}
            className="inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-sm font-semibold bg-brand-green text-[#04240f] hover:brightness-95 active:scale-[0.99] transition"
          >
            <Glyph icon={NavIcon.deposit} size={16} /> Deposit
          </button>
          <button
            onClick={() => navigate('/account')}
            className="w-8 h-8 rounded-full grid place-items-center bg-white/[0.06] border border-white/10 text-sm font-semibold hover:bg-white/10"
            aria-label="Account"
          >
            {(me?.user.firstName?.[0] ?? 'U').toUpperCase()}
          </button>
        </div>
      </header>

      {/* Body: left nav / main / right rail */}
      <div className="flex-1 grid overflow-hidden lg:grid-cols-[240px_minmax(0,1fr)] xl:grid-cols-[240px_minmax(0,1fr)_340px]">
        {/* Left nav */}
        <aside className="hidden lg:flex flex-col gap-1 overflow-y-auto border-r border-white/[0.07] bg-brand-navy2/60 p-3">
          <NavLink to="/bets" end className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition ${isActive ? 'bg-brand-teal/10 text-brand-teal' : 'text-white/70 hover:bg-white/[0.04]'}`}>
            <Glyph icon={NavIcon.home} size={18} /> Home
          </NavLink>
          {/* Account links sit directly under Home, above the sports list — the
              stakeholder's ask of 2026-10-20 (buttons were below the sports and
              scrolled off on shorter screens). */}
          <div className="px-3 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-white/30">My account</div>
          <NavLink to="/bets/history" className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition ${isActive ? 'text-brand-teal' : 'text-white/65 hover:bg-white/[0.04]'}`}>
            <Glyph icon={NavIcon.ticket} size={16} /> My bets
          </NavLink>
          <NavLink to="/savings" className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition ${isActive ? 'text-brand-teal' : 'text-white/65 hover:bg-white/[0.04]'}`}>
            <Glyph icon={NavIcon.savings} size={16} /> Savings
          </NavLink>
          <NavLink to="/account" className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition ${isActive ? 'text-brand-teal' : 'text-white/65 hover:bg-white/[0.04]'}`}>
            <Glyph icon={NavIcon.account} size={16} /> Account
          </NavLink>
          <NavLink to="/limits" className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition ${isActive ? 'text-brand-teal' : 'text-white/65 hover:bg-white/[0.04]'}`}>
            <Glyph icon={NavIcon.account} size={16} /> Limits &amp; controls
          </NavLink>
          <div className="px-3 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-white/30">Sports &amp; racing</div>
          {/* The sport id is carried in the URL, not in a handler-local variable:
              BetsPage reads ?sport= as its filter, so the nav and the page agree
              on one source of truth and a filtered view is linkable and
              back-button-able. The id here MUST match the `sport` value the feed
              returns (horse_racing, ufc_mma — not racing, ufc), or the filter
              silently matches nothing and the category reads as empty. */}
          {SPORTS.map((s) => (
            <button
              key={s.id}
              onClick={() => navigate(`/bets?sport=${encodeURIComponent(s.id)}`)}
              className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm text-white/65 hover:bg-white/[0.04] hover:text-white transition text-left"
            >
              <span className="text-white/45"><Glyph icon={s.icon} size={16} /></span>
              <span className="flex-1">{s.name}</span>
              {s.live && <span className="text-[9px] font-bold uppercase tracking-wide text-red-400 bg-red-400/10 rounded px-1.5 py-0.5">Live</span>}
            </button>
          ))}

          {/*
            The only entry point to the staff console. Shown when /me reports a
            role — null means ordinary customer, which is a value, not a missing
            one. This is a DISPLAY gate: hiding the link hides nothing, and the
            API re-checks the JWT claim on every admin request.
          */}
          {me?.user.role && (
            <NavLink to="/admin" className={({ isActive }) => `mt-1 flex items-center gap-3 rounded-xl border border-brand-gold/20 px-3 py-2 text-sm transition ${isActive ? 'text-brand-gold' : 'text-brand-gold/70 hover:bg-brand-gold/[0.06]'}`}>
              <Glyph icon={<><path d="M12 3 4 6v5.5c0 4.6 3.2 8.4 8 9.5 4.8-1.1 8-4.9 8-9.5V6l-8-3Z" /></>} size={16} /> Staff console
            </NavLink>
          )}
        </aside>

        {/* Main */}
        <main className="overflow-y-auto">
          <div className="max-w-3xl xl:max-w-none mx-auto w-full px-4 lg:px-7 py-6">
            <Outlet />
          </div>
          <Footer />
        </main>

        {/* Right rail */}
        <aside className="hidden xl:flex flex-col gap-3.5 overflow-y-auto border-l border-white/[0.07] bg-brand-navy2/60 p-4">
          <BetSlip variant="rail" />
          <div className="rounded-2xl border border-brand-green/20 bg-gradient-to-br from-[#0e2e1c] to-[#082014] p-4">
            <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-brand-green/80">Savings (locked)</div>
            <div className="mt-1 font-display text-3xl font-extrabold tabular-nums">{formatCents(savings)}</div>
            <p className="text-xs text-white/45 mt-2">Your money — returned at term, or unlocked early when a bet wins.</p>
          </div>
          <div className="rounded-2xl border border-brand-teal/20 bg-gradient-to-br from-[#102f3d] to-[#0a1c2c] p-4">
            <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-brand-teal/90">Balance · withdrawable</div>
            <div className="mt-1 font-display text-3xl font-extrabold tabular-nums">{formatCents(playable)}</div>
            <p className="text-xs text-white/45 mt-2">Grows when bets win. Bet with it or withdraw it.</p>
          </div>
          <div className="rounded-2xl border border-white/[0.08] bg-brand-surface/70 p-4">
            <div className="font-display font-bold text-[15px] mb-1.5">How it works</div>
            <p className="text-xs text-white/50 leading-relaxed">
              Your first deposit splits into locked Savings and a Play Balance. Win a bet and Savings
              unlocks into your withdrawable Balance — your money’s still yours, it just moves between
              your own buckets.
            </p>
          </div>
        </aside>
      </div>

      {/* Slip below `xl` (floating pill + bottom sheet) and the placement toast.
          Both live here, not in BetsPage, so a selection picked on the event
          page and a confirmation shown on any page both work. */}
      <BetSlipSheet />
      <BetPlacedToast />

      {/* Mobile bottom nav */}
      <nav className="lg:hidden shrink-0 border-t border-white/[0.07] bg-brand-navy/80 backdrop-blur-md flex">
        {mobileNav.map(({ to, label, icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) => `relative flex-1 flex flex-col items-center gap-1 py-2.5 text-[11px] font-medium transition ${isActive ? 'text-brand-teal' : 'text-white/40 hover:text-white/75'}`}
          >
            {({ isActive }) => (
              <>
                {isActive && <span className="absolute top-0 h-0.5 w-8 rounded-full bg-brand-teal shadow-glow" />}
                <Glyph icon={icon} size={22} />
                {label}
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
    </BetSlipProvider>
  );
}
